import { compoundInterest, tfsaGrowth, rrspGrowth, fhsaGrowth, respGrowth, gicReturn } from "../../lib/calculators/investments";
import { ACCOUNT_LIMITS, PROVINCE_LIST } from "../../lib/financial-data/canada";
import { CAD } from "../../lib/format";

const provinceOptions = PROVINCE_LIST.map((p) => ({ value: p.code, label: p.name }));
const growthChart = (series, extra = []) => ({
  type: "stackedArea",
  title: "Projected growth",
  data: series.map((s) => ({ x: `Yr ${s.year}`, Contributions: s.contributions, Growth: Math.max(0, (s.growth ?? (s.balance - s.contributions))) })),
  xKey: "x",
  series: [{ key: "Contributions", name: "Contributions" }, { key: "Growth", name: "Investment growth" }, ...extra],
});

export const compoundInterestConfig = {
  slug: "compound-interest-calculator-canada",
  name: "Compound Interest Calculator",
  category: "investing",
  icon: "TrendingUp",
  tagline: "See how your savings grow with the power of compounding.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["tfsa-growth-calculator-canada", "savings-goal-calculator-canada"],
  seo: {
    title: "Compound Interest Calculator Canada (2026) — Investment Growth",
    description: "Free compound interest calculator for Canadians. See how regular contributions and compounding grow your money over time, with a year-by-year chart.",
    keywords: ["compound interest calculator canada", "investment growth calculator", "compounding calculator"],
  },
  inputs: [
    { id: "principal", label: "Starting amount", type: "currency", default: 10000, min: 0 },
    { id: "monthlyContribution", label: "Monthly contribution", type: "currency", default: 500, min: 0 },
    { id: "annualRate", label: "Annual return", type: "percent", default: 6, min: 0, max: 30, step: 0.1, suffix: "%" },
    { id: "years", label: "Years", type: "slider", default: 25, min: 1, max: 50, step: 1, suffix: " yrs" },
    { id: "compoundsPerYear", label: "Compounding frequency", type: "select", default: 12, options: [{ value: 1, label: "Annually" }, { value: 4, label: "Quarterly" }, { value: 12, label: "Monthly" }] },
  ],
  compute: (v) => {
    const r = compoundInterest(v);
    return {
      primary: { label: `Value after ${v.years} years`, value: r.futureValue, type: "currency" },
      secondary: [
        { label: "Total contributions", value: r.totalContributions, type: "currency" },
        { label: "Interest earned", value: r.totalInterest, type: "currency" },
        { label: "Growth multiple", value: `${(r.futureValue / Math.max(1, r.totalContributions)).toFixed(2)}x`, type: "raw" },
      ],
      chart: growthChart(r.series),
      chart2: { type: "pie", title: "Contributions vs growth", data: [{ name: "Contributions", value: Math.round(r.totalContributions) }, { name: "Interest", value: Math.round(r.totalInterest) }] },
      downloadRows: () => [["Year", "Balance", "Contributions", "Growth"], ...r.series.map((s) => [s.year, s.balance, s.contributions, s.growth])],
    };
  },
  content: {
    howItWorks: "Compound interest means you earn returns on both your original money and the returns it has already generated. This calculator grows your starting amount plus regular contributions at your chosen rate and compounding frequency, showing the balance year by year.",
    inputsExplained: [
      { term: "Starting amount", desc: "The money you begin with today." },
      { term: "Monthly contribution", desc: "How much you add each month." },
      { term: "Annual return", desc: "Your expected average yearly return. Markets vary — use a conservative estimate." },
    ],
    example: "$10,000 plus $500/month at 6% for 25 years grows to roughly $380,000 — of which more than half is investment growth.",
    assumptions: ["A constant average return every year.", "Contributions made at period end.", "Returns are before tax and fees."],
    limitations: ["Real returns fluctuate year to year.", "Ignores inflation, taxes and fees.", "Not a guarantee of future performance."],
  },
  faq: [
    { q: "What return should I use?", a: "A diversified long-term portfolio has historically returned around 5–7% before inflation, but returns vary. Use a conservative figure and consider inflation." },
    { q: "Is this before or after tax?", a: "Before tax. In a TFSA growth is tax-free; in an RRSP it's tax-deferred. Try the TFSA and RRSP calculators for account-specific views." },
  ],
};

export const tfsaGrowthConfig = {
  slug: "tfsa-growth-calculator-canada",
  name: "TFSA Growth Calculator",
  category: "investing",
  icon: "TrendingUp",
  tagline: "Project tax-free growth in your Tax-Free Savings Account.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "tfsa-rrsp-planner",
  relatedTools: ["rrsp-growth-calculator-canada", "compound-interest-calculator-canada"],
  seo: {
    title: "TFSA Growth Calculator Canada (2026) — Tax-Free Investment Projection",
    description: `Project your TFSA growth in Canada. See tax-free investment growth year by year. ${ACCOUNT_LIMITS.tfsa.annual} annual limit for ${ACCOUNT_LIMITS.year}.`,
    keywords: ["tfsa calculator canada", "tfsa growth calculator", "tax free savings account calculator", "tfsa contribution limit 2026"],
  },
  inputs: [
    { id: "initial", label: "Current TFSA balance", type: "currency", default: 20000, min: 0 },
    { id: "annualContribution", label: "Annual contribution", type: "currency", default: 7000, min: 0, help: `${ACCOUNT_LIMITS.year} annual TFSA limit is ${CAD(ACCOUNT_LIMITS.tfsa.annual)}.` },
    { id: "contributionRoom", label: "Your available TFSA room (from CRA)", type: "currency", default: 7000, min: 0, help: "Total room available this year from CRA My Account. Defaults to the annual limit." },
    { id: "annualReturn", label: "Annual return", type: "percent", default: 6, min: 0, max: 30, step: 0.1, suffix: "%" },
    { id: "years", label: "Years", type: "slider", default: 25, min: 1, max: 50, step: 1, suffix: " yrs" },
  ],
  compute: (v) => {
    const r = tfsaGrowth(v);
    const room = v.contributionRoom ?? r.annualLimit;
    const over = (v.annualContribution || 0) - room;
    return {
      primary: { label: `Tax-free value after ${v.years} years`, value: r.futureValue, type: "currency" },
      secondary: [
        { label: "Total contributions", value: r.totalContributions, type: "currency" },
        { label: "Tax-free growth", value: r.totalGrowth, type: "currency" },
        { label: `${ACCOUNT_LIMITS.year} annual limit`, value: r.annualLimit, type: "currency" },
        { label: "Room used this year", value: room ? `${Math.min(100, Math.round(((v.annualContribution || 0) / room) * 100))}%` : "—", type: "raw", hint: over > 0 ? "Over your room" : "Within your room" },
      ],
      chart: growthChart(r.series),
      warning: over > 0
        ? `Your ${CAD(v.annualContribution)} annual contribution exceeds your available room of ${CAD(room)} by ${CAD(over)}. TFSA over-contributions are taxed 1% per month on the excess until withdrawn — confirm your room in CRA My Account before contributing.`
        : null,
      downloadRows: () => [["Year", "Balance", "Contributions", "Growth"], ...r.series.map((s) => [s.year, s.balance, s.contributions, s.growth])],
    };
  },
  content: {
    howItWorks: "A TFSA lets your investments grow completely tax-free, and withdrawals are never taxed. This tool projects how your balance grows from contributions and returns. Note: this is a growth projection — it estimates growth, not your official contribution room, which depends on your personal history and is tracked by CRA.",
    inputsExplained: [
      { term: "Current TFSA balance", desc: "What you already hold in your TFSA." },
      { term: "Annual contribution", desc: `How much you add each year. The ${ACCOUNT_LIMITS.year} annual limit is ${CAD(ACCOUNT_LIMITS.tfsa.annual)}.` },
    ],
    example: `Contributing the ${CAD(ACCOUNT_LIMITS.tfsa.annual)} annual maximum starting from $20,000 at 6% for 25 years grows to well over $400,000 — all tax-free.`,
    assumptions: ["Growth projection only, not contribution-room tracking.", "Constant annual return.", "Contributions stay within your available room."],
    limitations: ["Does not track your official CRA contribution room.", "Over-contributions are penalized by CRA — verify your room.", "Returns are not guaranteed."],
  },
  faq: [
    { q: "Does this track my TFSA contribution room?", a: "No. This is a growth projection. Your official room depends on your history since you turned 18 (and became a resident) and is shown in your CRA My Account." },
    { q: "Is TFSA growth really tax-free?", a: "Yes. Investment growth and withdrawals from a TFSA are not taxed in Canada." },
  ],
};

export const rrspGrowthConfig = {
  slug: "rrsp-growth-calculator-canada",
  name: "RRSP Growth Calculator",
  category: "investing",
  icon: "TrendingUp",
  tagline: "Project tax-deferred growth in your RRSP.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "tfsa-rrsp-planner",
  relatedTools: ["rrsp-tax-savings-calculator-canada", "retirement-calculator-canada"],
  seo: {
    title: "RRSP Growth Calculator Canada (2026) — Retirement Savings Projection",
    description: "Project your RRSP growth in Canada with tax-deferred compounding. See your retirement savings year by year.",
    keywords: ["rrsp calculator canada", "rrsp growth calculator", "registered retirement savings plan calculator"],
  },
  inputs: [
    { id: "initial", label: "Current RRSP balance", type: "currency", default: 30000, min: 0 },
    { id: "annualContribution", label: "Annual contribution", type: "currency", default: 8000, min: 0 },
    { id: "contributionRoom", label: "Your RRSP deduction limit (from CRA)", type: "currency", default: 0, min: 0, help: "Your available room from your CRA Notice of Assessment. Leave 0 to skip the room check." },
    { id: "annualReturn", label: "Annual return", type: "percent", default: 6, min: 0, max: 30, step: 0.1, suffix: "%" },
    { id: "years", label: "Years until retirement", type: "slider", default: 30, min: 1, max: 50, step: 1, suffix: " yrs" },
  ],
  compute: (v) => {
    const r = rrspGrowth(v);
    const room = v.contributionRoom || 0;
    const over = (v.annualContribution || 0) - room;
    let warning = null;
    if (room > 0 && over > 0) {
      warning = over > 2000
        ? `Your ${CAD(v.annualContribution)} contribution exceeds your ${CAD(room)} deduction limit by ${CAD(over)} — beyond the $2,000 lifetime over-contribution buffer. The excess is generally taxed 1% per month. Check your CRA Notice of Assessment.`
        : `Your contribution exceeds your ${CAD(room)} deduction limit by ${CAD(over)}, within the $2,000 lifetime over-contribution buffer (usually no penalty, but not deductible this year). Verify with CRA.`;
    }
    return {
      primary: { label: `RRSP value after ${v.years} years`, value: r.futureValue, type: "currency" },
      secondary: [
        { label: "Total contributions", value: r.totalContributions, type: "currency" },
        { label: "Tax-deferred growth", value: r.totalGrowth, type: "currency" },
        ...(room > 0 ? [{ label: "Deduction limit", value: room, type: "currency", hint: over > 0 ? "Exceeded" : "Within limit" }] : []),
      ],
      chart: growthChart(r.series),
      note: "RRSP withdrawals are taxed as income in retirement — plan withdrawals carefully.",
      warning,
      downloadRows: () => [["Year", "Balance", "Contributions", "Growth"], ...r.series.map((s) => [s.year, s.balance, s.contributions, s.growth])],
    };
  },
  content: {
    howItWorks: "An RRSP defers tax: contributions are deductible now and grow tax-free until withdrawal, when they're taxed as income. This tool projects your balance from contributions and returns. Pair it with the RRSP Tax Savings calculator to see your refund.",
    inputsExplained: [
      { term: "Current RRSP balance", desc: "What you already hold in your RRSP." },
      { term: "Annual contribution", desc: `Room is 18% of prior-year earned income up to ${CAD(ACCOUNT_LIMITS.rrsp.maxDollar)} for ${ACCOUNT_LIMITS.year}.` },
    ],
    example: "$30,000 plus $8,000/year at 6% for 30 years grows to roughly $800,000 before tax.",
    assumptions: ["Growth projection only.", "Constant annual return.", "Contributions within your available room."],
    limitations: ["Withdrawals are taxed as income.", "Does not model RRIF conversion at 71.", "Room depends on your income and pension adjustments."],
  },
  faq: [
    { q: "How much can I contribute to an RRSP?", a: `Your room is 18% of prior-year earned income, up to ${CAD(ACCOUNT_LIMITS.rrsp.maxDollar)} for ${ACCOUNT_LIMITS.year}, minus any pension adjustment. Check your CRA My Account.` },
    { q: "When is RRSP money taxed?", a: "On withdrawal, as regular income. The idea is you contribute at a high marginal rate now and withdraw at a lower rate in retirement." },
  ],
};

export const fhsaConfig = {
  slug: "fhsa-calculator-canada",
  name: "FHSA Calculator",
  category: "investing",
  icon: "TrendingUp",
  tagline: "Grow a tax-free down payment with the First Home Savings Account.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "tfsa-rrsp-planner",
  relatedTools: ["tfsa-growth-calculator-canada", "mortgage-affordability-calculator-canada"],
  seo: {
    title: "FHSA Calculator Canada (2026) — First Home Savings Account Growth & Tax Savings",
    description: `Project your FHSA growth and tax savings. ${CAD(ACCOUNT_LIMITS.fhsa.annual)}/year, ${CAD(ACCOUNT_LIMITS.fhsa.lifetime)} lifetime limit. Tax-deductible like an RRSP, tax-free like a TFSA.`,
    keywords: ["fhsa calculator canada", "first home savings account", "fhsa contribution limit", "fhsa tax savings"],
  },
  inputs: [
    { id: "initial", label: "Current FHSA balance", type: "currency", default: 0, min: 0 },
    { id: "annualContribution", label: "Annual contribution", type: "currency", default: 8000, min: 0, help: `Max ${CAD(ACCOUNT_LIMITS.fhsa.annual)}/year, ${CAD(ACCOUNT_LIMITS.fhsa.lifetime)} lifetime.` },
    { id: "annualReturn", label: "Annual return", type: "percent", default: 5, min: 0, max: 30, step: 0.1, suffix: "%" },
    { id: "years", label: "Years", type: "slider", default: 5, min: 1, max: 15, step: 1, suffix: " yrs" },
    { id: "annualIncome", label: "Annual income (for tax savings)", type: "currency", default: 80000, min: 0 },
    { id: "province", label: "Province", type: "select", default: "ON", options: provinceOptions },
  ],
  compute: (v) => {
    const r = fhsaGrowth(v);
    const annualOver = (v.annualContribution || 0) - r.annualLimit;
    const lifetimeContrib = (v.annualContribution || 0) * (v.years || 0) + (v.initial || 0);
    const lifeOver = lifetimeContrib - r.lifetimeLimit;
    let warning = null;
    if (annualOver > 0) {
      warning = `Your ${CAD(v.annualContribution)} annual contribution exceeds the ${CAD(r.annualLimit)} FHSA annual limit by ${CAD(annualOver)}. Over-contributions are taxed 1% per month — confirm your room with CRA.`;
    } else if (lifeOver > 0) {
      warning = `Your planned contributions total ${CAD(lifetimeContrib)} over ${v.years} years, exceeding the ${CAD(r.lifetimeLimit)} FHSA lifetime limit by ${CAD(lifeOver)}. Reduce contributions or shorten the timeline.`;
    }
    return {
      primary: { label: `FHSA value after ${v.years} years`, value: r.futureValue, type: "currency" },
      secondary: [
        { label: "Total contributions", value: r.totalContributions, type: "currency" },
        { label: "Tax-free growth", value: r.totalGrowth, type: "currency" },
        { label: "Est. annual tax refund", value: r.annualTaxRefund, type: "currency", hint: "From the deduction" },
        { label: "Annual limit", value: r.annualLimit, type: "currency" },
        { label: "Lifetime limit", value: r.lifetimeLimit, type: "currency", hint: `${CAD(lifetimeContrib)} planned` },
      ],
      chart: growthChart(r.series),
      warning,
      downloadRows: () => [["Year", "Balance", "Contributions", "Growth"], ...r.series.map((s) => [s.year, s.balance, s.contributions, s.growth])],
    };
  },
  content: {
    howItWorks: "The FHSA combines the best of both worlds: contributions are tax-deductible (like an RRSP) AND qualifying withdrawals for a first home are tax-free (like a TFSA). You can contribute up to $8,000/year and $40,000 total. This tool projects growth and estimates your annual tax refund from the deduction.",
    inputsExplained: [
      { term: "Annual contribution", desc: `Up to ${CAD(ACCOUNT_LIMITS.fhsa.annual)} per year, ${CAD(ACCOUNT_LIMITS.fhsa.lifetime)} lifetime.` },
      { term: "Annual income", desc: "Used to estimate the tax refund from your deductible contributions." },
    ],
    example: "Contributing $8,000/year for 5 years at 5% grows to about $46,000 tax-free toward your first home, plus yearly tax refunds along the way.",
    assumptions: ["Withdrawals are for a qualifying first-home purchase (tax-free).", "Tax refund estimated at your marginal rate ignoring credits.", "You have opened an FHSA and have room."],
    limitations: ["Must be a first-time home buyer to open an FHSA.", "Non-qualifying withdrawals are taxable.", "15-year account lifetime applies."],
  },
  faq: [
    { q: "Can I use both the FHSA and the RRSP Home Buyers' Plan?", a: "Yes. As of recent rules you can combine an FHSA withdrawal with the RRSP Home Buyers' Plan for the same home purchase." },
    { q: "What if I don't buy a home?", a: "You can transfer FHSA funds to an RRSP/RRIF tax-free (without using RRSP room), or withdraw them as taxable income." },
  ],
};

export const respConfig = {
  slug: "resp-calculator-canada",
  name: "RESP Calculator",
  category: "investing",
  icon: "GraduationCap",
  tagline: "Grow education savings with the Canada Education Savings Grant.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "family-financial-planner",
  relatedTools: ["compound-interest-calculator-canada", "savings-goal-calculator-canada"],
  seo: {
    title: "RESP Calculator Canada (2026) — Education Savings + CESG Grant",
    description: "Project RESP growth including the 20% Canada Education Savings Grant (up to $500/year, $7,200 lifetime). Plan your child's education fund.",
    keywords: ["resp calculator canada", "cesg calculator", "registered education savings plan", "education savings canada"],
  },
  inputs: [
    { id: "initial", label: "Current RESP balance", type: "currency", default: 0, min: 0 },
    { id: "annualContribution", label: "Annual contribution", type: "currency", default: 2500, min: 0, help: "Contributing $2,500/year maximizes the annual $500 CESG grant." },
    { id: "annualReturn", label: "Annual return", type: "percent", default: 6, min: 0, max: 30, step: 0.1, suffix: "%" },
    { id: "years", label: "Years until college", type: "slider", default: 18, min: 1, max: 25, step: 1, suffix: " yrs" },
    { id: "cesgRate", label: "CESG match rate", type: "percent", default: 20, min: 0, max: 40, step: 1, suffix: "%", help: "Standard grant is 20% (configurable)." },
    { id: "cesgAnnualMax", label: "CESG annual max", type: "currency", default: 500, min: 0 },
  ],
  compute: (v) => {
    const r = respGrowth({ ...v, cesgRate: (v.cesgRate || 0) / 100 });
    return {
      primary: { label: `RESP value after ${v.years} years`, value: r.futureValue, type: "currency" },
      secondary: [
        { label: "Your contributions", value: r.totalContributions, type: "currency" },
        { label: "Government grants (CESG)", value: r.totalGrants, type: "currency" },
        { label: "Investment growth", value: r.totalGrowth, type: "currency" },
      ],
      chart: {
        type: "stackedArea",
        title: "RESP growth: contributions + grants + returns",
        data: r.series.map((s) => ({ x: `Yr ${s.year}`, Contributions: s.contributions, Grants: s.grants, Growth: Math.max(0, s.balance - s.contributions - s.grants) })),
        xKey: "x",
        series: [{ key: "Contributions", name: "Contributions" }, { key: "Grants", name: "CESG grants" }, { key: "Growth", name: "Growth" }],
      },
      downloadRows: () => [["Year", "Balance", "Contributions", "Grants"], ...r.series.map((s) => [s.year, s.balance, s.contributions, s.grants])],
    };
  },
  content: {
    howItWorks: "An RESP grows education savings tax-deferred, and the government adds the Canada Education Savings Grant (CESG) — 20% on the first $2,500 you contribute each year, up to $500/year and $7,200 lifetime per child. This calculator layers your contributions, the grant, and investment growth.",
    inputsExplained: [
      { term: "Annual contribution", desc: "Contributing $2,500/year captures the full $500 annual CESG." },
      { term: "CESG match rate", desc: "Standard 20% federal match, editable for lower-income top-ups (A-CESG)." },
    ],
    example: "$2,500/year for 18 years at 6% grows to roughly $95,000, including about $7,200 in CESG grants.",
    assumptions: ["CESG at 20% up to $500/year, $7,200 lifetime.", "Constant return.", "Contributions within the $50,000 lifetime limit."],
    limitations: ["Additional CESG and provincial grants not fully modelled.", "Growth (not contributions) is taxed in the student's hands on withdrawal.", "Grant rules can change."],
  },
  faq: [
    { q: "How much grant can I get?", a: "The basic CESG is 20% on up to $2,500/year — a maximum of $500/year and $7,200 per child over the account's life." },
    { q: "Is RESP money taxed?", a: "Your contributions come out tax-free. Grants and growth are taxed in the student's hands, usually at a low rate." },
  ],
};

export const gicConfig = {
  slug: "gic-calculator-canada",
  name: "GIC Calculator",
  category: "investing",
  icon: "Lock",
  tagline: "Calculate guaranteed returns on a GIC.",
  applicableYear: 2026,
  lastUpdated: "2026-01-01",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["compound-interest-calculator-canada", "emergency-fund-calculator-canada"],
  seo: {
    title: "GIC Calculator Canada (2026) — Guaranteed Investment Certificate Returns",
    description: "Calculate the maturity value and interest of a Canadian GIC. Compare simple vs compound interest and different compounding frequencies.",
    keywords: ["gic calculator canada", "guaranteed investment certificate", "gic interest calculator", "gic maturity value"],
  },
  inputs: [
    { id: "principal", label: "Investment amount", type: "currency", default: 10000, min: 0 },
    { id: "annualRate", label: "GIC interest rate", type: "percent", default: 4.5, min: 0, max: 20, step: 0.05, suffix: "%" },
    { id: "years", label: "Term", type: "slider", default: 5, min: 1, max: 10, step: 1, suffix: " yrs" },
    { id: "compoundsPerYear", label: "Compounding", type: "select", default: 1, options: [{ value: 1, label: "Annually" }, { value: 2, label: "Semi-annually" }, { value: 12, label: "Monthly" }] },
    { id: "interestType", label: "Interest type", type: "select", default: "compound", options: [{ value: "compound", label: "Compound" }, { value: "simple", label: "Simple" }] },
  ],
  compute: (v) => {
    const r = gicReturn({ principal: v.principal, annualRate: v.annualRate, years: v.years, compoundsPerYear: v.compoundsPerYear, simple: v.interestType === "simple" });
    return {
      primary: { label: `Maturity value after ${v.years} years`, value: r.maturityValue, type: "currency2" },
      secondary: [
        { label: "Principal", value: r.principal, type: "currency" },
        { label: "Interest earned", value: r.totalInterest, type: "currency2" },
        { label: "Total return", value: `${((r.totalInterest / Math.max(1, r.principal)) * 100).toFixed(1)}%`, type: "raw" },
      ],
      chart: { type: "line", title: "Value over time", data: r.series.map((s) => ({ x: `Yr ${s.year}`, Value: s.balance })), xKey: "x", series: [{ key: "Value", name: "GIC value" }] },
      note: "GIC interest is fully taxable as income each year (unless held in a TFSA/RRSP).",
      downloadRows: () => [["Year", "Value"], ...r.series.map((s) => [s.year, s.balance])],
    };
  },
  content: {
    howItWorks: "A GIC (Guaranteed Investment Certificate) pays a fixed, guaranteed rate over a set term. This tool computes your maturity value using either compound interest (interest earns interest) or simple interest (interest on principal only), at your chosen compounding frequency.",
    inputsExplained: [
      { term: "GIC rate", desc: "The guaranteed annual rate the issuer pays." },
      { term: "Compounding", desc: "How often interest is added. More frequent compounding yields slightly more." },
      { term: "Interest type", desc: "Compounding GICs reinvest interest; simple-interest GICs pay it out." },
    ],
    example: "$10,000 in a 5-year GIC at 4.5% compounded annually matures at about $12,462 — roughly $2,462 in interest.",
    assumptions: ["Rate is fixed for the full term.", "Interest reinvested for compound GICs.", "Returns shown before tax."],
    limitations: ["GIC interest is fully taxable if held outside a registered account.", "Funds are usually locked until maturity (non-redeemable GICs).", "Does not account for inflation eroding purchasing power."],
  },
  faq: [
    { q: "Are GICs safe?", a: "GICs are low-risk and often insured by CDIC up to limits. The trade-off is lower returns than stocks and possible loss of purchasing power to inflation." },
    { q: "Is GIC interest taxed?", a: "Yes, annually as regular income — unless the GIC is held inside a TFSA, RRSP or FHSA." },
  ],
};
